/**
 * Generieke cross-sell add-on (snippets/product-addon.liquid).
 *
 * Hangt zijn regel aan het 'cart:add:collect'-event dat product-form.js synchroon afvuurt
 * vóór het versturen (HBJ-insert daar). Alle add-ons plus het hoofdproduct gaan daardoor in
 * één /cart/add; de sections-render van product-form.js bevat de add-ons al.
 *
 * Tot 17/10/2026 deed dit bestand hetzelfde via een capture-fase submit-interceptie die het
 * form annuleerde, zelf /cart/add.js aanriep en daarna her-submitte — bij twee add-ons drie
 * opeenvolgende requests en twee re-submits. Het event-protocol vervangt die keten.
 * Faalt de gecombineerde add (bv. add-on uitverkocht), dan doet product-form.js één retry
 * met alleen het hoofdproduct; de klant raakt zijn hoofdaankoop dus nooit kwijt.
 *
 * Optioneel: tier-keuze (radio's .js-addon-tier, elk een ander product) en per tier
 * een kleurkeuze (.js-addon-colors[data-tier]). Zonder tiers is er hooguit één
 * kleur-fieldset.
 */

if (!customElements.get('product-addon')) {
  class ProductAddon extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('.js-addon-checkbox');
      this.tiersFieldset = this.querySelector('.js-addon-tiers');
      this.colorFieldsets = [...this.querySelectorAll('.js-addon-colors')];

      const sectionEl = this.closest('.shopify-section');
      this.form = sectionEl
        ? sectionEl.querySelector('form.js-product-form-main:not([id*="quickadd"])')
        : null;

      if (!this.form || !this.checkbox) {
        this.hidden = true;
        return;
      }

      this.checkbox.addEventListener('change', () => this.syncVisibility());
      this.addEventListener('change', (evt) => {
        if (evt.target.classList.contains('js-addon-tier')) this.syncVisibility();
        if (evt.target.classList.contains('js-addon-color')) this.updateColorLabels();
      });
      this.updateColorLabels();
      this.syncVisibility();

      this.collectHandler = this.handleCollect.bind(this);
      this.addedHandler = this.handleAdded.bind(this);
      document.addEventListener('cart:add:collect', this.collectHandler);
      document.addEventListener('on:cart:add', this.addedHandler);
      document.addEventListener('on:line-item:change', this.addedHandler);
    }

    disconnectedCallback() {
      if (this.collectHandler) {
        document.removeEventListener('cart:add:collect', this.collectHandler);
      }
      if (this.addedHandler) {
        document.removeEventListener('on:cart:add', this.addedHandler);
        document.removeEventListener('on:line-item:change', this.addedHandler);
      }
    }

    selectedTier() {
      return this.querySelector('.js-addon-tier:checked');
    }

    /**
     * Toont tier-keuze en de kleur-fieldset van de gekozen tier alleen bij aangevinkt.
     */
    syncVisibility() {
      const checked = this.checkbox.checked;
      if (this.tiersFieldset) this.tiersFieldset.hidden = !checked;
      const tier = this.selectedTier();
      this.colorFieldsets.forEach((fieldset) => {
        const belongsToTier = !tier || fieldset.dataset.tier === tier.dataset.tier;
        fieldset.hidden = !(checked && belongsToTier);
      });
    }

    updateColorLabels() {
      this.colorFieldsets.forEach((fieldset) => {
        const selected = fieldset.querySelector('.js-addon-color:checked');
        const label = fieldset.querySelector('.js-addon-color-label');
        if (selected && label) {
          label.textContent = `Kleur: ${selected.dataset.colorTitle}`;
        }
      });
    }

    selectedVariantId() {
      const tier = this.selectedTier();
      const fieldset = tier
        ? this.colorFieldsets.find((f) => f.dataset.tier === tier.dataset.tier)
        : this.colorFieldsets[0];
      const color = fieldset ? fieldset.querySelector('.js-addon-color:checked') : null;
      if (color) return Number(color.value);
      if (tier) return Number(tier.value);
      return Number(this.dataset.variantId);
    }

    /**
     * Hangt de aangevinkte add-on aan de items-lijst van het hoofdformulier.
     * @param {object} evt - 'cart:add:collect'-event van product-form.js.
     */
    handleCollect(evt) {
      if (!evt.detail || evt.detail.form !== this.form) return;
      if (!this.checkbox.checked) return;

      const variantId = this.selectedVariantId();
      if (!variantId) return;

      evt.detail.items.push({ id: variantId, quantity: 1 });
      this.contributed = true;
    }

    /**
     * Vinkje terugzetten zodra de toevoeging geslaagd is.
     */
    handleAdded() {
      if (!this.contributed) return;
      this.contributed = false;
      this.checkbox.checked = false;
      this.syncVisibility();
    }
  }

  customElements.define('product-addon', ProductAddon);
}
